import { useMemo } from 'react';
import type { SelectedProduct } from '@nfa/types';

interface WeightChartProps {
  items: SelectedProduct[];
}

/**
 * Categories that are considered consumables (variable weight during trip)
 * These are excluded from base weight calculations
 */
const CONSUMABLE_CATEGORIES = new Set(['water', 'food', 'fuel']);

/**
 * Color palette for category bars
 * Each category gets a consistent color across renders
 */
const CATEGORY_COLORS: Record<string, string> = {
  shelter: 'bg-orange-500',
  backpack: 'bg-blue-500',
  sleeping_bag: 'bg-purple-500',
  sleeping_pad: 'bg-indigo-500',
  rain_jacket: 'bg-cyan-500',
  rain_trousers: 'bg-teal-500',
  insulation: 'bg-rose-500',
  midlayer: 'bg-pink-500',
  hiking_trousers: 'bg-emerald-500',
  footwear: 'bg-amber-500',
  socks: 'bg-lime-500',
  cooking: 'bg-red-500',
  water: 'bg-sky-500',
  navigation: 'bg-violet-500',
  lighting: 'bg-yellow-500',
  trekking_poles: 'bg-green-500',
  first_aid: 'bg-fuchsia-500',
  default: 'bg-neutral-500',
};

/**
 * Get color class for a category
 */
function getCategoryColor(categoryId: string): string {
  return CATEGORY_COLORS[categoryId] || CATEGORY_COLORS.default;
}

/**
 * Format weight for display
 */
function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

/**
 * Aggregate categories with their total weights
 */
interface CategoryWeight {
  id: string;
  name: string;
  weight: number;
  isConsumable: boolean;
}

/**
 * Weight breakdown chart showing category distribution
 *
 * Displays a horizontal bar chart of weight by category,
 * distinguishing between base weight and consumables.
 * Mobile responsive with vertical stacking on small screens.
 */
export function WeightChart({ items }: WeightChartProps) {
  // Aggregate weights by category
  const categoryWeights = useMemo((): CategoryWeight[] => {
    const categoryMap = new Map<string, { name: string; weight: number; isConsumable: boolean }>();

    for (const item of items) {
      const existing = categoryMap.get(item.category_id);
      if (existing) {
        existing.weight += item.weight_g;
      } else {
        categoryMap.set(item.category_id, {
          name: item.category_name,
          weight: item.weight_g,
          isConsumable: CONSUMABLE_CATEGORIES.has(item.category_id),
        });
      }
    }

    // Convert to array and sort by weight (heaviest first)
    return Array.from(categoryMap.entries())
      .map(([id, data]) => ({
        id,
        name: data.name,
        weight: data.weight,
        isConsumable: data.isConsumable,
      }))
      .sort((a, b) => b.weight - a.weight);
  }, [items]);

  // Calculate totals
  const { totalWeight, baseWeight, maxWeight } = useMemo(() => {
    let total = 0;
    let base = 0;
    let max = 0;

    for (const category of categoryWeights) {
      total += category.weight;
      if (!category.isConsumable) {
        base += category.weight;
      }
      if (category.weight > max) {
        max = category.weight;
      }
    }

    return { totalWeight: total, baseWeight: base, maxWeight: max };
  }, [categoryWeights]);

  // Empty state
  if (items.length === 0) {
    return (
      <div
        data-testid="weight-chart"
        aria-label="Weight breakdown chart - no items"
        className="flex flex-col sm:flex-row p-4 bg-neutral-900 border border-neutral-800 rounded-xl animate-fadeIn"
      >
        <h3 className="font-mono text-lg font-semibold text-white mb-4">Weight Breakdown</h3>
        <p className="text-neutral-500">No items in kit</p>
        <p className="text-neutral-400 text-sm">0kg</p>
      </div>
    );
  }

  return (
    <div
      data-testid="weight-chart"
      aria-label={`Weight breakdown chart showing ${categoryWeights.length} categories totaling ${formatWeight(totalWeight)}`}
      className="flex flex-col sm:flex-row p-4 bg-neutral-900 border border-neutral-800 rounded-xl animate-fadeIn"
    >
      {/* Header with totals */}
      <div className="mb-4 sm:mb-0 sm:mr-6 sm:w-48 shrink-0">
        <h3 className="font-mono text-lg font-semibold text-white mb-3">Weight Breakdown</h3>

        <div className="space-y-2">
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider">Base Weight</p>
            <p
              data-testid="base-weight-value"
              className="font-mono text-xl font-bold text-orange-500"
            >
              {formatWeight(baseWeight)}
            </p>
          </div>

          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider">Total</p>
            <p data-testid="total-weight-value" className="font-mono text-lg text-white">
              {formatWeight(totalWeight)}
            </p>
          </div>
        </div>
      </div>

      {/* Category bars */}
      <div className="flex-1 space-y-2">
        {categoryWeights.map((category) => {
          const percentage = Math.round((category.weight / totalWeight) * 100);
          const barWidth = maxWeight > 0 ? Math.round((category.weight / maxWeight) * 100) : 0;

          return (
            <div
              key={category.id}
              data-testid="category-row"
              data-consumable={category.isConsumable ? 'true' : undefined}
              className="flex items-center gap-3"
            >
              {/* Category name */}
              <span className="w-24 text-sm text-neutral-300 truncate">{category.name}</span>

              {/* Bar */}
              <div className="flex-1 h-6 bg-neutral-800 rounded overflow-hidden">
                <div
                  data-testid={`category-bar-${category.id}`}
                  className={`h-full transition-all duration-500 ease-out ${getCategoryColor(category.id)} ${
                    category.isConsumable ? 'opacity-60' : ''
                  }`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>

              {/* Weight and percentage */}
              <span className="w-14 text-sm text-neutral-400 text-right">
                {formatWeight(category.weight)}
              </span>
              <span className="w-10 text-xs text-neutral-500 text-right">{percentage}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
